import {
  CalendarBlank,
  Clock,
  CloudCheck,
  MapPin,
  Note,
  Users,
  X,
} from '@phosphor-icons/react';
import clsx from 'clsx';
import { Badge, IconButton, Typography } from '@la/ds-ui-components';
import { isGameActivity } from 'lib/utils/schedules/isGameActivity';
import {
  ActivityState,
  ActivityWithTeamNames,
} from 'domains/ActivityForm/openapi-client/models';
import formatNotes from '../utils/formatNotes';
import { useColorClassName } from '../utils/useColorClassName';
import { useSiteLevelCalendarDetails } from '../utils/useSiteLevelCalendarDetails';
import styles from './SiteLevelCalendarActivityDetails.module.scss';

export type SiteLevelCalendarActivityDetailsProps = {
  activity: ActivityWithTeamNames;
  togglePopover: React.Dispatch<React.SetStateAction<boolean>>;
};

export function SiteLevelCalendarActivityDetails({
  activity,
  togglePopover,
}: SiteLevelCalendarActivityDetailsProps) {
  const activityDetailProps = useSiteLevelCalendarDetails({ activity });

  const { colorHex, colorName } = useColorClassName(
    activityDetailProps ? activityDetailProps.activityProgramId : 0
  );

  if (!activityDetailProps) {
    return <></>;
  }

  const isCanceled = activity.state === ActivityState.Canceled;

  const {
    activityType,
    arrivalTime,
    awayTeamName,
    awayTeamPrimaryStaffName,
    endDate,
    endTime,
    locationName,
    isAllDayActivity,
    primaryStaffName,
    programName,
    notes,
    startDate,
    startTime,
    teamName,
    title,
    isHomeTeamExternal,
    isAwayTeamExternal,
    isExternalActivity,
    activityProgramId,
  } = activityDetailProps;

  const isSiteWideEvent = activityProgramId < 1;
  const activityProgramName = isSiteWideEvent ? 'Sitewide' : programName;
  const activityDetailsTitle =
    title !== '' ? title : `${teamName} ${activityType}`;
  const isGame = isGameActivity(activity.type);
  const programColor = `colorIcon-${colorName}`;

  const hasDateRange = endDate && startDate !== endDate;
  const formattedNotes = notes ? formatNotes(notes) : '';

  return (
    <div className={styles.activityDetailsContainer}>
      <div className={styles.toolBar}>
        <IconButton
          ariaLabel="Close"
          icon={<X />}
          onClick={() => togglePopover(false)}
          variant="text"
        />
      </div>

      <div
        className={clsx({
          [styles.titleContainer]: true,
          [styles.isCanceled]: isCanceled,
        })}
      >
        <div
          className={clsx({
            [styles.colorIcon]: true,
            [styles.colorIconCanceled]: isCanceled,
            [styles[programColor]]: true,
          })}
        />
        <Typography variant="ui" size="large" weight="bold">
          {activityProgramName}
        </Typography>
      </div>

      <div
        className={clsx({
          [styles.titleContainer]: true,
          [styles.isCanceled]: isCanceled,
        })}
      >
        {isGame ? (
          <div
            className={clsx({
              [styles.gameTitle]: true,
              [styles.isCanceled]: isCanceled,
            })}
          >
            <div
              className={clsx({
                [styles.externalTeamContainer]: isHomeTeamExternal,
              })}
            >
              <Typography variant="ui" size="small" weight="bold">
                {teamName}
              </Typography>
            </div>{' '}
            <div className={styles.gameVs}>
              <Typography variant="ui" size="small">
                vs
              </Typography>
            </div>{' '}
            <div
              className={clsx({
                [styles.externalTeamContainer]: isAwayTeamExternal,
              })}
            >
              <Typography variant="ui" size="small" weight="bold">
                {awayTeamName}
              </Typography>
            </div>
          </div>
        ) : (
          <Typography variant="ui" size="small" weight="bold">
            {activityDetailsTitle}
          </Typography>
        )}
      </div>

      <div
        className={clsx({
          [styles.iconTextContainer]: true,
          [styles.isCanceled]: isCanceled,
        })}
      >
        <CalendarBlank size={14} />
        <Typography variant="ui" size="small" weight="regular">
          {isAllDayActivity && hasDateRange
            ? `${startDate} - ${endDate}`
            : `${startDate}`}
        </Typography>
      </div>
      {isAllDayActivity ? null : (
        <div
          className={clsx({
            [styles.iconTextContainer]: true,
            [styles.isCanceled]: isCanceled,
          })}
        >
          <Clock size={14} />
          <Typography variant="ui" size="small" weight="regular">
            {startTime} - {endTime}
            {arrivalTime ? <>{`, arrive at ${arrivalTime}`}</> : null}
          </Typography>
        </div>
      )}
      <div
        className={clsx({
          [styles.iconTextContainer]: true,
          [styles.isCanceled]: isCanceled,
        })}
      >
        <MapPin />
        <Typography variant="ui" size="small" weight="regular">
          {locationName}
        </Typography>
      </div>

      {teamName ? (
        <div className={styles.iconTextContainer}>
          <Users />
          <div
            className={clsx({
              [styles.isCanceled]: isCanceled,
            })}
          >
            <Typography variant="ui" size="small" weight="regular">
              {teamName}
              {primaryStaffName ? ` | ${primaryStaffName}` : null}
              {awayTeamName ? ',' : null}
            </Typography>
            {awayTeamName ? (
              <Typography variant="ui" size="small" weight="regular">
                {awayTeamName}{' '}
                {awayTeamPrimaryStaffName
                  ? `| ${awayTeamPrimaryStaffName}`
                  : null}
              </Typography>
            ) : null}
          </div>
        </div>
      ) : null}
      {notes ? (
        <div
          className={clsx({
            [styles.iconTextContainer]: true,
            [styles.isCanceled]: isCanceled,
          })}
        >
          <Note size={14} />
          <Typography variant="ui" size="small" weight="regular">
            {formattedNotes}
          </Typography>
        </div>
      ) : null}
      <div className={styles.badgesContainer}>
        <div className={styles.badgeContainerBase}>
          <Badge text={activityType} size="medium" />
        </div>
        {isExternalActivity && (
          <div className={styles.syncedActivity}>
            <CloudCheck size={14} fill={colorHex} />
            <Typography variant="ui" size="xs" weight="regular">
              LAF Synced
            </Typography>
          </div>
        )}
        {isCanceled ? (
          <div className={styles.cancelBadgeContainer}>
            <Badge text="Canceled" size="medium" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
